import {
  applyEmailNotVerifiedRestriction,
  EmailNotVerifiedError,
  handleUnauthorized,
  isEmailNotVerifiedPayload,
  isSessionEmailUnverified,
} from '@utils/apiHelpers'

/**
 * Treat a cached ticket as stale this far before backend expiry
 * (`expiresIn` / `expiresAt`, typically 3600s). Do not hardcode the TTL.
 */
const TOKEN_EXPIRY_SKEW_MS = 30_000
/**
 * v20 — response drives mode via `authMode`. `connect_proxy` → `data.ticket`
 * only (Centrifugo connect proxy issues connect.token server-side). `jwt` →
 * connection JWT only. Ticket and JWT are mutually exclusive on the wire; the
 * previous fallback that sent both simultaneously has been removed.
 *
 * v19 — proxy-only: connect ticket via POST /api/realtime/connect-ticket
 *       → Centrifuge `data.ticket`.
 * v18 — connect-ticket + optional /api/token JWT fallback.
 * v17 — ticket-only in data (3501 on this Centrifugo).
 * v16 — ticket as token (3500).
 * v15 — ticket as token, no connect proxy.
 * v14 — POST /api/token JWT cache.
 * v13 — Berlin TZ connection JWT `subs`.
 */
export const CENTRIFUGO_CACHE_STORAGE_KEY = 'centrifuge_token_cache_v20'
export const CENTRIFUGO_CONNECT_TICKET_PATH = '/api/realtime/connect-ticket'
const CONNECT_TICKET_KIND = 'connect-ticket'

export type CachedTokenEntry = {
  token?: string
  connectData?: unknown
  expiresAt: number
  kind?: string
}

export type CentrifugeConnectMode = 'jwt' | 'proxy'

export type CentrifugeConnectCredentials = {
  mode: CentrifugeConnectMode
  token: string
  connectData: Record<string, unknown> | null
  expiresAt: number
}

/** The signed pass from /api/realtime/connect-ticket — centrifuge `data.ticket` only. */
export function ticketFromCredentials(creds: CentrifugeConnectCredentials): string {
  if (creds.connectData && typeof creds.connectData.ticket === 'string') {
    return creds.connectData.ticket.trim()
  }
  return ''
}

/** Centrifugo connection JWT for `connect.token`. Never the connect-ticket pass. */
export function centrifugoJwtFromCredentials(creds: CentrifugeConnectCredentials): string {
  const token = String(creds.token ?? '').trim()
  if (!token || classifyConnectJwt(token) === 'pass') return ''
  return token
}

type ConnectionBucket = {
  key: string
  entry: CachedTokenEntry | null
  inflight: Promise<CentrifugeConnectCredentials> | null
  /** Bumped on invalidate so a stale in-flight fetch cannot overwrite a newer token. */
  gen: number
}

const connectionBuckets = new Map<string, ConnectionBucket>()
const channelCache = new Map<string, CachedTokenEntry>()
const channelInflight = new Map<string, Promise<string>>()
const channelGen = new Map<string, number>()

/** Bumped on logout / account switch so a late response cannot populate the new cache. */
let cacheEpoch = 0

const TOKEN_FETCH_TIMEOUT_MS = 12_000

function fingerprint(value: string): string {
  let hash = 2166136261
  for (let i = 0; i < value.length; i += 1) {
    hash ^= value.charCodeAt(i)
    hash = Math.imul(hash, 16777619)
  }
  return (hash >>> 0).toString(36)
}

function authorizationFrom(headers: Record<string, string>): string {
  return headers.Authorization ?? headers.authorization ?? ''
}

function sessionKey(baseUrl: string, headers: Record<string, string>): string {
  const base = baseUrl.replace(/\/$/, '')
  return `${base}|${fingerprint(authorizationFrom(headers))}`
}

function connectionStorageKey(session: string): string {
  return `${CENTRIFUGO_CACHE_STORAGE_KEY}|conn|${session}`
}

function channelStorageKey(session: string, channel: string): string {
  return `${CENTRIFUGO_CACHE_STORAGE_KEY}|sub|${session}|${channel}`
}

function storageRead(key: string): CachedTokenEntry | null {
  try {
    const raw = sessionStorage.getItem(key)
    if (!raw) return null
    const entry: CachedTokenEntry = JSON.parse(raw)
    if (!entry || entry.expiresAt <= Date.now()) {
      sessionStorage.removeItem(key)
      return null
    }
    return entry
  } catch {
    return null
  }
}

function storageWrite(key: string, entry: CachedTokenEntry): void {
  try {
    sessionStorage.setItem(key, JSON.stringify(entry))
  } catch {
    /* quota exceeded */
  }
}

function clearSessionStorageCache(): void {
  try {
    for (let i = sessionStorage.length - 1; i >= 0; i -= 1) {
      const key = sessionStorage.key(i)
      if (key && key.startsWith('centrifuge_token_cache_')) sessionStorage.removeItem(key)
    }
  } catch {
    /* ignore */
  }
}

function bucketFor(session: string): ConnectionBucket {
  const existing = connectionBuckets.get(session)
  if (existing) return existing
  const created: ConnectionBucket = { key: session, entry: null, inflight: null, gen: 0 }
  connectionBuckets.set(session, created)
  return created
}

function readConnectionEntry(session: string): CachedTokenEntry | null {
  const bucket = bucketFor(session)
  if (
    bucket.entry &&
    bucket.entry.expiresAt > Date.now() &&
    bucket.entry.kind === CONNECT_TICKET_KIND
  ) {
    return bucket.entry
  }
  const stored = storageRead(connectionStorageKey(session))
  if (stored && stored.kind === CONNECT_TICKET_KIND) {
    bucket.entry = stored
    return stored
  }
  bucket.entry = null
  return null
}

function writeConnectionEntry(session: string, entry: CachedTokenEntry, epoch: number): void {
  if (epoch !== cacheEpoch) return
  const bucket = bucketFor(session)
  if (bucket.key !== session) return
  bucket.entry = entry
  storageWrite(connectionStorageKey(session), entry)
}

function readChannelToken(session: string, channel: string): string | null {
  const key = channelStorageKey(session, channel)
  const mem = channelCache.get(key)
  if (mem && mem.expiresAt > Date.now()) return mem.token ?? null
  const stored = storageRead(key)
  if (stored) {
    channelCache.set(key, stored)
    return stored.token ?? null
  }
  return null
}

function writeChannelToken(
  session: string,
  channel: string,
  token: string,
  expiresAt: number,
  epoch: number,
): void {
  if (epoch !== cacheEpoch) return
  const key = channelStorageKey(session, channel)
  const entry: CachedTokenEntry = { token, expiresAt }
  channelCache.set(key, entry)
  storageWrite(key, entry)
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

function jwtExpiryMs(token: string): number | null {
  try {
    const payloadPart = token.split('.')[1]
    if (!payloadPart) return null
    const json = atob(payloadPart.replace(/-/g, '+').replace(/_/g, '/'))
    const payload = JSON.parse(json) as { exp?: number }
    if (typeof payload.exp !== 'number' || !Number.isFinite(payload.exp)) return null
    return payload.exp * 1000
  } catch {
    return null
  }
}

type JwtClass = 'pass' | 'connection' | 'unknown'

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const payloadPart = token.split('.')[1]
    if (!payloadPart) return null
    return JSON.parse(atob(payloadPart.replace(/-/g, '+').replace(/_/g, '/'))) as Record<
      string,
      unknown
    >
  } catch {
    return null
  }
}

function classifyConnectJwt(token: string): JwtClass {
  const payload = decodeJwtPayload(token)
  if (!payload) return 'unknown'
  const kind = String(payload.kind ?? '').toLowerCase()
  const aud = Array.isArray(payload.aud)
    ? payload.aud.map(String)
    : payload.aud != null
      ? [String(payload.aud)]
      : []
  if (kind.includes('connect') || aud.some((a) => a.includes('realtime-connect'))) return 'pass'
  if (payload.subs && typeof payload.subs === 'object') return 'connection'
  return 'unknown'
}

function resolveExpiresAt(raw: Record<string, unknown> | null, token: string): number {
  const now = Date.now()
  const candidates = [
    raw?.expiresIn,
    raw?.expires_in,
    raw?.ttl,
    raw?.expiresAt,
    raw?.expires_at,
    raw?.expireAt,
  ]
  for (const value of candidates) {
    const n = Number(value)
    if (!Number.isFinite(n) || n <= 0) continue
    // Absolute epoch (ms or seconds) vs duration in seconds (e.g. backend 3600).
    if (n > 1e12) return n
    if (n > 1e9) return n * 1000
    return now + n * 1000
  }
  const jwtExp = token ? jwtExpiryMs(token) : null
  if (jwtExp && jwtExp > now) return jwtExp
  // No expiresIn / expiresAt / JWT exp — do not assume an hour; refresh on next connect.
  return now
}

function withSkew(expiresAt: number): number {
  const skewed = expiresAt - TOKEN_EXPIRY_SKEW_MS
  const minValid = Date.now() + 5_000
  return Math.max(skewed, minValid)
}

function ticketString(value: unknown): string {
  return typeof value === 'string' && value.trim() ? value.trim() : ''
}

/** Centrifuge-js calls Subscription `getToken({ channel })`, not `getToken(channel)`. */
export function centrifugeChannelFromGetTokenArg(arg: unknown): string {
  if (typeof arg === 'string') return arg.trim()
  const rec = asRecord(arg)
  if (rec && rec.channel != null) return String(rec.channel).trim()
  return ''
}

function pickSubscribeToken(rec: Record<string, unknown> | null): string {
  if (!rec) return ''
  for (const key of ['token', 'subscribeToken', 'subscriptionToken', 'channelToken', 'jwt']) {
    const value = ticketString(rec[key])
    if (value) return value
  }
  return ''
}

function warnIfDialogTokenUnreadable(channel: string, token: string): void {
  try {
    const payloadPart = token.split('.')[1]
    if (!payloadPart) {
      console.warn('[centrifugeTokens] dialog-token is not a JWT', channel)
      return
    }
    JSON.parse(atob(payloadPart.replace(/-/g, '+').replace(/_/g, '/')))
  } catch {
    console.warn('[centrifugeTokens] dialog-token claims unreadable', channel)
  }
}

/** Connect-proxy payload: identity pass goes in `data.ticket`. */
function connectDataFromTicket(ticket: string): Record<string, unknown> {
  return { ticket }
}

function parseConnectData(raw: Record<string, unknown> | null): Record<string, unknown> | null {
  if (!raw) return null
  const direct = ticketString(raw.ticket)
  if (direct) return connectDataFromTicket(direct)

  const candidate = raw.connectData ?? raw.connectionData ?? raw.connect
  const asTicket = ticketString(candidate)
  if (asTicket) return connectDataFromTicket(asTicket)

  const rec = asRecord(candidate)
  if (rec) {
    const nested = ticketString(rec.ticket)
    if (nested) return connectDataFromTicket(nested)
    if (Object.keys(rec).length > 0) return rec
  }
  return null
}

function dropLegacyJwtCache(): void {
  try {
    for (let i = sessionStorage.length - 1; i >= 0; i -= 1) {
      const key = sessionStorage.key(i)
      if (!key || !key.startsWith('centrifuge_token_cache_')) continue
      if (key.startsWith(CENTRIFUGO_CACHE_STORAGE_KEY)) continue
      sessionStorage.removeItem(key)
    }
  } catch {
    /* ignore */
  }
}

dropLegacyJwtCache()

function credentialsFromEntry(entry: CachedTokenEntry): CentrifugeConnectCredentials | null {
  if (entry.kind !== CONNECT_TICKET_KIND) return null
  if (entry.expiresAt <= Date.now()) return null
  const connectData = asRecord(entry.connectData)
  const ticket = ticketString(connectData?.ticket)
  if (ticket) {
    // Cached tickets always mean proxy mode; even if a JWT was cached beside
    // one from an older build we must drop it — sending both is what caused
    // the hybrid `connect.data.ticket` + `connect.token` frames in production.
    return {
      mode: 'proxy',
      token: '',
      connectData: connectDataFromTicket(ticket),
      expiresAt: entry.expiresAt,
    }
  }
  const jwt =
    ticketString(entry.token) && classifyConnectJwt(String(entry.token)) !== 'pass'
      ? String(entry.token).trim()
      : ''
  if (jwt) {
    return { mode: 'jwt', token: jwt, connectData: null, expiresAt: entry.expiresAt }
  }
  if (connectData && Object.keys(connectData).length > 0) {
    return { mode: 'proxy', token: '', connectData, expiresAt: entry.expiresAt }
  }
  return null
}

function readAuthMode(data: Record<string, unknown>): 'proxy' | 'jwt' | null {
  const raw = String(
    data.authMode ?? data.auth_mode ?? data.mode ?? asRecord(data.auth)?.mode ?? '',
  )
    .trim()
    .toLowerCase()
  if (!raw) return null
  if (raw === 'connect_proxy' || raw === 'connect-proxy' || raw === 'proxy') return 'proxy'
  if (raw === 'jwt' || raw === 'connect_jwt' || raw === 'connection_jwt') return 'jwt'
  return null
}

function parseCredentialsPayload(json: unknown): CentrifugeConnectCredentials {
  const root = asRecord(json)
  const nested = asRecord(root?.data)
  const data = { ...(root ?? {}), ...(nested ?? {}) }
  const ticket =
    ticketString(data.ticket) || ticketString(asRecord(data.connectData)?.ticket)
  const namedJwt =
    ticketString(data.connectionToken) ||
    ticketString(data.centrifugoToken) ||
    ticketString(data.connection_token)
  const rawToken = ticketString(data.token)
  const rawJwtCandidate =
    namedJwt ||
    (rawToken && rawToken !== ticket && classifyConnectJwt(rawToken) !== 'pass'
      ? rawToken
      : '')
  const declaredMode = readAuthMode(data)
  // authMode from backend wins. When absent we infer, but a ticket ALWAYS
  // implies proxy — never fall back to sending JWT alongside a ticket.
  const mode: CentrifugeConnectMode =
    declaredMode ?? (ticket ? 'proxy' : rawJwtCandidate ? 'jwt' : 'proxy')

  const jwt = mode === 'jwt' ? rawJwtCandidate : ''
  const pass = mode === 'proxy'
    ? ticket || (classifyConnectJwt(rawToken) === 'pass' ? rawToken : '')
    : ''
  const expiresAt = withSkew(resolveExpiresAt(data, jwt || pass))
  return {
    mode,
    token: jwt,
    connectData:
      mode === 'proxy'
        ? pass
          ? connectDataFromTicket(pass)
          : parseConnectData(data)
        : null,
    expiresAt,
  }
}

/**
 * Drop all connection/subscribe tokens. Call on logout and when the auth session changes.
 */
export function clearCentrifugeTokenCache(): void {
  cacheEpoch += 1
  connectionBuckets.clear()
  channelCache.clear()
  channelInflight.clear()
  clearSessionStorageCache()
}

/** Drop the cached connection JWT/ticket for this API session so the next fetch hits backend. */
export function invalidateCentrifugeConnectionCache(
  baseUrl: string,
  headers: Record<string, string>,
): void {
  const session = sessionKey(baseUrl, headers)
  const bucket = connectionBuckets.get(session)
  if (bucket) {
    bucket.entry = null
    bucket.inflight = null
    bucket.gen += 1
  }
  try {
    sessionStorage.removeItem(connectionStorageKey(session))
  } catch {
    /* ignore */
  }
}

/** Drop a cached dialog.<id> subscribe token (e.g. after accept — claims change). */
export function invalidateCentrifugeChannelToken(
  baseUrl: string,
  headers: Record<string, string>,
  channel: string,
): void {
  const session = sessionKey(baseUrl, headers)
  const key = channelStorageKey(session, channel)
  channelCache.delete(key)
  channelInflight.delete(`${session}|${channel}`)
  channelGen.set(key, (channelGen.get(key) ?? 0) + 1)
  try {
    sessionStorage.removeItem(key)
  } catch {
    /* ignore */
  }
}

/**
 * Expected operator channels (documentation / JWT probe only).
 * Do NOT send this list to POST /api/token — backend embeds `subs` from auth.
 */
export function buildConnectionChannels(
  widgetIds: string[],
  operatorId: number | string,
): string[] {
  const channels: string[] = [
    `operator.${operatorId}.dialogs.feed`,
    `operator.${operatorId}.notifications`,
    `operator.${operatorId}.presence`,
    `operator.${operatorId}.visitors.feed`,
    `operator.${operatorId}.settings`,
    `operator.${operatorId}.billing`,
    `counters.my.${operatorId}`,
  ]
  widgetIds.forEach((id) => {
    channels.push(`widget.${id}.counters`)
    channels.push(`widget.${id}.dialogs.inbox`)
  })
  return channels
}

async function fetchJsonWithTimeout(
  url: string,
  init: RequestInit,
  timeoutMs: number = TOKEN_FETCH_TIMEOUT_MS,
): Promise<Response> {
  const controller = new AbortController()
  const timer = window.setTimeout(() => controller.abort(), timeoutMs)
  try {
    return await fetch(url, { ...init, signal: controller.signal })
  } finally {
    window.clearTimeout(timer)
  }
}

/**
 * POST /api/realtime/connect-ticket — identity pass for `data.ticket`
 * (Centrifugo connect proxy). No JWT fallback; the operator connection is
 * proxy-only. Putting the pass in `token` → 3500.
 *
 * Call before every connect attempt. Concurrent callers for the same session
 * share one in-flight request. A still-valid cache is reused — no setInterval
 * and no connect-ticket per dialog. `force` skips a valid cache entry (claims
 * changed) but still joins an already started fetch.
 */
export async function getCentrifugeConnectionCredentials(
  baseUrl: string,
  headers: Record<string, string>,
  opts?: { force?: boolean },
): Promise<CentrifugeConnectCredentials> {
  if (isSessionEmailUnverified()) {
    throw new EmailNotVerifiedError()
  }
  const session = sessionKey(baseUrl, headers)
  const epoch = cacheEpoch
  const bucket = bucketFor(session)

  if (!opts?.force) {
    const cached = readConnectionEntry(session)
    if (cached) {
      const creds = credentialsFromEntry(cached)
      if (creds) return creds
    }
  }

  if (bucket.inflight) return bucket.inflight

  if (opts?.force) {
    bucket.entry = null
    bucket.gen += 1
    try {
      sessionStorage.removeItem(connectionStorageKey(session))
    } catch {
      /* ignore */
    }
  }

  const gen = bucket.gen

  let promise!: Promise<CentrifugeConnectCredentials>
  promise = (async () => {
    try {
      const base = baseUrl.replace(/\/$/, '')
      const res = await fetchJsonWithTimeout(`${base}${CENTRIFUGO_CONNECT_TICKET_PATH}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...headers,
        },
        body: JSON.stringify({}),
      })

      if (handleUnauthorized(res)) {
        throw new Error(`Connect ticket request failed: ${res.status}`)
      }

      const json: unknown = await res.json().catch(() => null)
      const payload = asRecord(json)

      if (isEmailNotVerifiedPayload(json)) {
        applyEmailNotVerifiedRestriction(payload)
        throw new EmailNotVerifiedError(payload ? String(payload.message || payload.error || '') : '')
      }

      if (!res.ok) {
        const failMessage =
          (payload && (ticketString(payload.message) || ticketString(payload.error))) ||
          `Connect ticket request failed: ${res.status}`
        throw new Error(failMessage)
      }

      if (payload && payload.success === false) {
        const failMessage =
          ticketString(payload.message) ||
          ticketString(payload.error) ||
          'Connect ticket request failed'
        throw new Error(failMessage)
      }

      const creds = parseCredentialsPayload(json)

      if (epoch !== cacheEpoch) return creds
      if (sessionKey(baseUrl, headers) !== session) return creds
      if (bucket.gen !== gen) return creds

      // Cache only the half that matches the declared mode. Storing a JWT
      // beside a proxy ticket is what surfaced later as hybrid connect frames.
      const entry: CachedTokenEntry =
        creds.mode === 'proxy'
          ? {
              token: undefined,
              connectData: creds.connectData ?? undefined,
              expiresAt: creds.expiresAt,
              kind: CONNECT_TICKET_KIND,
            }
          : {
              token: creds.token || undefined,
              connectData: undefined,
              expiresAt: creds.expiresAt,
              kind: CONNECT_TICKET_KIND,
            }
      if (entry.connectData || entry.token) writeConnectionEntry(session, entry, epoch)
      return creds
    } finally {
      if (bucket.inflight === promise) bucket.inflight = null
    }
  })()

  bucket.inflight = promise
  return promise
}

/**
 * @deprecated Berlin TZ: per-channel subscribe via POST /api/token is removed.
 * Operator/widget channels come from connection JWT `subs`.
 * For dialog.<id> use {@link getDialogSubscriptionToken}.
 *
 * Kept as a stub so legacy call sites fail soft (empty token → no client sub).
 */
export async function getCentrifugeSubscriptionToken(
  _baseUrl: string,
  _headers: Record<string, string>,
  channel: string,
): Promise<string> {
  console.warn(
    '[centrifugeTokens] getCentrifugeSubscriptionToken is deprecated (Berlin TZ). ' +
      'Do not client-subscribe; use connection JWT subs. channel=',
    channel,
  )
  return ''
}

/**
 * POST /api/dialog-token — subscribe token for dialog.<id> only.
 * Body: { chatId }. Backend signs `dialog.<id>`, not `dialog.<id>.internal`.
 */
export async function getDialogSubscriptionToken(
  baseUrl: string,
  headers: Record<string, string>,
  channel: string,
  opts?: { force?: boolean },
): Promise<string> {
  const channelName = centrifugeChannelFromGetTokenArg(channel) || String(channel ?? '').trim()
  const internal = /^dialog\.(\d+)\.internal$/.exec(channelName)
  if (internal) {
    throw new Error(
      `dialog-token does not cover ${channelName}; wait for a server-side sub or a dedicated endpoint`,
    )
  }

  const match = channelName.match(/^dialog\.(\d+)$/)
  if (!match) {
    throw new Error(`Invalid dialog channel: ${channelName}`)
  }

  const session = sessionKey(baseUrl, headers)
  const epoch = cacheEpoch
  if (opts?.force) {
    invalidateCentrifugeChannelToken(baseUrl, headers, channelName)
  } else {
    const cached = readChannelToken(session, channelName)
    if (cached) return cached
    const inflightKey = `${session}|${channelName}`
    const existing = channelInflight.get(inflightKey)
    if (existing) return existing
  }

  const inflightKey = `${session}|${channelName}`
  const storageKey = channelStorageKey(session, channelName)
  const gen = channelGen.get(storageKey) ?? 0
  const chatId = Number(match[1])
  let promise!: Promise<string>
  promise = (async () => {
    try {
      if (isSessionEmailUnverified()) {
        throw new EmailNotVerifiedError()
      }
      const res = await fetchJsonWithTimeout(`${baseUrl.replace(/\/$/, '')}/api/dialog-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...headers,
        },
        body: JSON.stringify({ chatId }),
      })

      if (!res.ok) {
        throw new Error(`Dialog token request failed: ${res.status}`)
      }

      const json = await res.json()
      const data = asRecord(json) && 'data' in (asRecord(json) ?? {})
        ? asRecord((json as { data?: unknown }).data) ?? asRecord(json) ?? {}
        : asRecord(json) ?? {}
      const nested = asRecord(data.data) ?? data
      const token =
        pickSubscribeToken(nested) || pickSubscribeToken(data) || pickSubscribeToken(asRecord(json))
      const expiresAt = withSkew(resolveExpiresAt(nested, token))
      if (token) warnIfDialogTokenUnreadable(channelName, token)

      if (
        token &&
        epoch === cacheEpoch &&
        sessionKey(baseUrl, headers) === session &&
        (channelGen.get(storageKey) ?? 0) === gen
      ) {
        writeChannelToken(session, channelName, token, expiresAt, epoch)
      }
      return token
    } finally {
      if (channelInflight.get(inflightKey) === promise) {
        channelInflight.delete(inflightKey)
      }
    }
  })()

  channelInflight.set(inflightKey, promise)
  return promise
}
